<script>
    import {eventBridge, portalConfiguration} from "../../stores.js";
    import {writable} from "svelte/store";
    import {Button, Collapse, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter} from "sveltestrap";
    import ButtonLine from "../snippets/ButtonLine.svelte";
    import InputPin from "../form/InputPin.svelte";
    import InputFile from "../form/InputFile.svelte";
    import Help from "../snippets/Help.svelte";
    import {createEventDispatcher, onMount} from "svelte";
    import ConfirmModal from "./ConfirmModal.svelte";
    import Fa from "svelte-fa";
    import {faTriangleExclamation} from "@fortawesome/free-solid-svg-icons";

    const dispatch = createEventDispatcher();
    let refSubmitButton;
    export let showModal = writable(false);
    const showAlreadyExists = writable(false);
    const toggle = () => ($showModal = !$showModal);

    let validPin = true;
    let validPinRepeat = true;

    let optionalNewPin = false;
    const pin = writable('');
    const newPin = writable('');
    const newPinRepeat = writable('');
    let fileName;
    let fileExtension;
    let fileBytes;

    $: if (!$showModal) {
        validPin = true;
        validPinRepeat = true;
        optionalNewPin = false;
        $pin = '';
        $newPin = '';
        $newPinRepeat = '';
        fileName = undefined;
        fileExtension = undefined;
        fileBytes = undefined;
    }

    function handleSubmit() {
        if (optionalNewPin && $newPin !== $newPinRepeat) {
            validPinRepeat = false;
            return;
        }

        validPin = true;
        validPinRepeat = true;

        $eventBridge.dispatchEvent('cs:addTokenToCache', {
            fileExtension: fileExtension,
            fileBytes: Array.from(fileBytes),
            pin: $pin,
            optionalCachePin: optionalNewPin ? $newPin : null
        });
        dispatch("submit");
    }

    function handleFileChange(event) {
        fileName = event.detail.fileName;
        fileExtension = event.detail.fileExtension;
        fileBytes = event.detail.fileBytes;
    }

    function handleInvalidPin() {
        validPin = false;
    }

    function handleAlreadyExists() {
        $showAlreadyExists = true;
    }

    function handleSuccess() {
        $pin = '';
        $showModal = false;
    }

    $: requiredFieldsFilled = fileName && $pin && (!optionalNewPin || ($newPin && $newPinRepeat));

    onMount(() => {
        $eventBridge.addEventListener('cs:ErrorAddTokenInvalidPin', handleInvalidPin);
        $eventBridge.addEventListener('cs:ErrorAddTokenAlreadyExists', handleAlreadyExists);
        $eventBridge.addEventListener('cs:SuccessAddToken', handleSuccess);

        return () => {
            $eventBridge.removeEventListener('cs:ErrorAddTokenInvalidPin', handleInvalidPin);
            $eventBridge.removeEventListener('cs:ErrorAddTokenAlreadyExists', handleAlreadyExists);
            $eventBridge.removeEventListener('cs:SuccessAddToken', handleSuccess);
        };
    });
</script>

<Modal {...$$restProps} backdrop="static" header="{fileName ? 'PIN Eingabe' : 'Zertifikatsauswahl'}" isOpen={$showModal} scrollable size="lg" {toggle}>
    <ModalBody>
        <Form on:submit={(event) => {event.preventDefault(); handleSubmit();}}>
            <InputFile accept=".p12" autoOpen hidden on:cancel={() => $showModal = false} on:change={handleFileChange}/>

            {#if fileName}
                <FormGroup>
                    <Label>Bitte geben Sie die PIN des ausgewählten Zertifikats ein.</Label>
                    <InputPin bind:pin={$pin} invalid={!validPin} autofocus={fileName} placeholder='Ihre PIN für das ausgewählte Zertifikat "{fileName}".' data-test-id="addTokenModal-form-input-pin"/>
                </FormGroup>
                <Input type="checkbox" label="PIN neu vergeben (optional)" on:change={(event) => optionalNewPin = event.target.checked} data-test-id="addTokenModal-form-checkbox-optionalNewPin" />
                <Collapse isOpen={optionalNewPin} style="overflow-x:visible; padding-top: 4px;">
                    <FormGroup>
                        <Label>Neue PIN</Label>
                        <InputPin bind:pin={$newPin} placeholder="Neue PIN festlegen" data-test-id="addTokenModal-form-input-newPin" />
                        <Label>Neue PIN Wiederholung</Label>
                        <InputPin bind:pin={$newPinRepeat} invalid={!validPinRepeat} formFeedback="Die eingegebenen PINs stimmen nicht überein." placeholder="Wiederholung der neuen PIN" data-test-id="addTokenModal-form-input-newPinRepeat" />
                    </FormGroup>
                </Collapse>
            {:else}
                <p>Bitte wählen Sie die Zertifikatsdatei (*.p12) in dem bereits geöffneten Dialog aus.</p>
            {/if}
            <Button type="submit" bind:inner={refSubmitButton} default hidden disabled={!requiredFieldsFilled} color="primary" data-test-id="addTokenModal-form-button-ok">OK</Button>
        </Form>
    </ModalBody>

    <ModalFooter>
        {#if fileName}
            <ButtonLine>
                <Help slot="left" url="{$portalConfiguration.help ?? ''}/einrichtung-von-bea/bea-client-security/authentifizieren/pin-eingabe-bei-software-zertifikat-bzw-token" data-test-id="addTokenModal-form-help" />
                <Button on:click={() => $showModal = false} color="primary" data-test-id="addTokenModal-form-button-cancel">Abbrechen</Button>
                <Button on:click={() => refSubmitButton.click()} disabled={!requiredFieldsFilled} color="primary" data-test-id="addTokenModal-form-button-ok">OK</Button>
            </ButtonLine>
        {/if}
    </ModalFooter>
</Modal>

<ConfirmModal showConfirm={showAlreadyExists} on:confirm={() => {$showModal = false}}>
    <svelte:fragment slot="header">
        <Fa icon="{faTriangleExclamation}" />Fehler
    </svelte:fragment>

    Dieses Software-Token ist bereits vorhanden.
</ConfirmModal>

